// ── Version history (owner 2026-09-13) ──────────────────────────────────────────────────────────
// A list of version numbers; clicking one shows what changed on the LEFT (owner's spec). The data
// is the git ledger (every commit after the baseline = one version), so nothing here is typed by
// hand. Visible to every logged-in user — no module permission.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../utils/api';

type Entry = { version: string; date: number; commit: string; subject: string; body: string };

const fmtDate = (ts: number) => {
  if (!ts) return '';
  const d = new Date(ts * 1000);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

export default function VersionPage() {
  const { t } = useTranslation();
  const [system, setSystem] = useState('');
  const [entries, setEntries] = useState<Entry[]>([]);
  const [sel, setSel] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.versions().then((d: any) => {
      setSystem(d.system || '');
      const list: Entry[] = (d.entries || []).slice().reverse();   // newest first
      setEntries(list);
      if (list.length) setSel(list[0].version);
    }).catch(() => setEntries([])).finally(() => setLoading(false));
  }, []);

  const cur = entries.find(e => e.version === sel) || null;
  const latest = entries[0]?.version || '';

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-app)' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 20px', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
        <img className="app-logo" src="/logo.png" alt="SLF" style={{ width: 28, height: 28, objectFit: 'contain' }} />
        <b style={{ fontSize: 15.5, color: 'var(--text-primary)' }}>{t('ver_title')}</b>
        {!!system && <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{system}</span>}
        <div style={{ flex: 1 }} />
        {!!latest && (
          <span style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--accent)', border: '1.5px solid var(--accent)', borderRadius: 999, padding: '3px 12px' }}>
            {t('ver_current')}: {latest}
          </span>
        )}
      </header>

      {loading ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-tertiary)' }}>
          <i className="ti ti-loader" style={{ fontSize: 24 }} /></div>
      ) : !entries.length ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-tertiary)', fontSize: 13.5 }}>{t('ver_empty')}</div>
      ) : (
        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* LEFT — what this version changed (owner: "点击每个版本号,屏幕左边显示系统修改了什么"). */}
          <div style={{ flex: 1, overflow: 'auto', padding: '22px 28px' }}>
            {cur ? (
              <div style={{ maxWidth: 860 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 4 }}>
                  <span style={{ fontSize: 24, fontWeight: 900, color: 'var(--text-primary)' }}>{cur.version}</span>
                  {cur.version === latest && <span style={{ fontSize: 10.5, fontWeight: 800, color: '#166534', background: '#E8F6EE', borderRadius: 999, padding: '2px 9px' }}>{t('ver_latest')}</span>}
                  <span style={{ fontSize: 12.5, color: 'var(--text-tertiary)' }}>{fmtDate(cur.date)} · {cur.commit}</span>
                </div>
                <div style={{ fontSize: 15.5, fontWeight: 700, color: 'var(--text-primary)', margin: '10px 0 14px' }}>{cur.subject}</div>
                {!!cur.body?.trim() && (
                  <div style={{ border: '1px solid var(--border)', borderRadius: 12, background: 'var(--bg-surface)', padding: '16px 20px', fontSize: 13, lineHeight: 1.65, color: 'var(--text-secondary)', whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>
                    {cur.body.trim()}
                  </div>
                )}
              </div>
            ) : <div style={{ color: 'var(--text-tertiary)', fontSize: 13 }}>{t('ver_pick')}</div>}
          </div>

          {/* RIGHT — the version list. */}
          <div style={{ width: 280, borderLeft: '1px solid var(--border)', background: 'var(--bg-surface)', overflow: 'auto' }}>
            {entries.map(e => {
              const active = e.version === sel;
              return (
                <button key={e.version + e.commit} onClick={() => setSel(e.version)}
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '11px 16px', border: 'none', borderBottom: '1px solid var(--border)', cursor: 'pointer', background: active ? 'var(--bg-active)' : 'transparent' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 13.5, fontWeight: 800, color: active ? 'var(--accent)' : 'var(--text-primary)' }}>{e.version}</span>
                    {e.version === latest && <span style={{ fontSize: 9.5, fontWeight: 800, color: '#166534', background: '#E8F6EE', borderRadius: 999, padding: '1px 7px' }}>{t('ver_latest')}</span>}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>{fmtDate(e.date)}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.subject}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
